import React, { useState, useEffect, useCallback } from 'react';
import { useVideoStore, VIDEO_CATEGORIES } from '../store/VideoContext';
import { clearCache } from '../services/api';

/**
 * Custom hook for fetching and managing video data
 * @param {string} category - Video category to fetch
 * @param {Object} options - Fetch options
 * @returns {Object} Hook state and methods
 */
export const useVideos = (category, options = {}) => {
  const { autoFetch = true, ...fetchOptions } = options;
  const { actions, selectors } = useVideoStore();
  
  const videos = selectors.getVideosByCategory(category);
  const loading = selectors.isLoading(category);
  const error = selectors.getError(category);
  const hasMore = selectors.hasMore(category);
  const pagination = selectors.getPagination(category);

  // Fetch videos
  const fetchVideos = useCallback(
    (customOptions = {}) => {
      return actions.fetchVideosByCategory(category, { ...fetchOptions, ...customOptions });
    },
    [actions, category, fetchOptions]
  );

  // Load more videos
  const loadMore = useCallback(
    (customOptions = {}) => {
      return actions.loadMoreVideos(category, { ...fetchOptions, ...customOptions });
    },
    [actions, category, fetchOptions]
  );

  // Refresh videos
  const refresh = useCallback(() => {
    actions.resetCategory(category);
    return fetchVideos();
  }, [actions, category, fetchVideos]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && videos.length === 0 && !loading && !error) {
      fetchVideos();
    }
  }, [autoFetch, videos.length, loading, error, fetchVideos]);

  return {
    videos,
    loading,
    error,
    hasMore,
    pagination,
    fetchVideos,
    loadMore,
    refresh,
    clearError: () => actions.clearError(category)
  };
};

/**
 * Hook for featured video
 * @param {string} pathseo - Video pathseo to fetch as featured
 * @param {Object} options - Options
 * @returns {Object} Hook state and methods
 */
export const useFeaturedVideo = (pathseo, options = {}) => {
  const { autoFetch = true } = options;
  const { actions, selectors } = useVideoStore();
  
  const featuredVideo = selectors.getFeaturedVideo();
  const loading = selectors.isLoading(VIDEO_CATEGORIES.FEATURED);
  const error = selectors.getError(VIDEO_CATEGORIES.FEATURED);

  const fetchFeaturedVideo = useCallback(
    (customPathseo) => {
      return actions.fetchFeaturedVideo(customPathseo || pathseo);
    },
    [actions, pathseo]
  );

  useEffect(() => {
    if (autoFetch && pathseo && !featuredVideo && !loading && !error) {
      fetchFeaturedVideo();
    }
  }, [autoFetch, pathseo, featuredVideo, loading, error, fetchFeaturedVideo]);

  return {
    featuredVideo,
    loading,
    error,
    fetchFeaturedVideo,
    clearError: () => actions.clearError(VIDEO_CATEGORIES.FEATURED)
  };
};

/**
 * Hook for managing current video (used in modals, players, etc.)
 * @returns {Object} Current video state and methods
 */
export const useCurrentVideo = () => {
  const { actions, selectors } = useVideoStore();
  
  const currentVideo = selectors.getCurrentVideo();

  const setCurrentVideo = useCallback(
    (video) => {
      actions.setCurrentVideo(video);
    },
    [actions]
  );

  const clearCurrentVideo = useCallback(() => {
    actions.setCurrentVideo(null);
  }, [actions]);

  return {
    currentVideo,
    setCurrentVideo,
    clearCurrentVideo
  };
};

/**
 * Hook for search functionality
 * @param {string} initialQuery - Initial search query
 * @returns {Object} Search state and methods
 */
export const useVideoSearch = (initialQuery = '') => {
  const [query, setQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { actions } = useVideoStore();

  const search = useCallback(
    async (searchQuery, category = VIDEO_CATEGORIES.POPULAR_MOVIES) => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await actions.fetchVideosByCategory(category, {
          search: searchQuery,
          limit: 20
        });
        setSearchResults(results);
      } catch (err) {
        setError(err.message);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    [actions]
  );

  const clearSearch = useCallback(() => {
    setQuery('');
    setSearchResults([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (query) {
      const timeoutId = setTimeout(() => {
        search(query);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [query, search]);

  return {
    query,
    setQuery,
    searchResults,
    loading,
    error,
    search,
    clearSearch
  };
};

/**
 * Hook for infinite scroll functionality
 * @param {string} category - Video category
 * @param {Object} options - Options
 * @returns {Object} Infinite scroll state and methods
 */
export const useInfiniteVideos = (category, options = {}) => {
  const { threshold = 100 } = options;
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const videoHook = useVideos(category, options);

  const loadMore = useCallback(async () => {
    if (videoHook.hasMore && !videoHook.loading && !isLoadingMore) {
      setIsLoadingMore(true);
      try {
        await videoHook.loadMore();
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [videoHook.hasMore, videoHook.loading, videoHook.loadMore, isLoadingMore]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight + threshold) {
        loadMore();
      }
    },
    [loadMore, threshold]
  );

  return {
    ...videoHook,
    isLoadingMore,
    loadMore,
    handleScroll
  };
};

/**
 * Hook for managing video categories with batch loading
 * @param {Array} categories - Array of categories to manage
 * @param {Object} options - Options
 * @returns {Object} Multi-category state and methods
 */
export const useMultipleVideoCategories = (categories = [], options = {}) => {
  const { autoFetch = true, theme = null, language = null, ...otherOptions } = options;
  const { actions, selectors } = useVideoStore();
  
  const data = categories.reduce((acc, category) => {
    acc[category] = {
      videos: selectors.getVideosByCategory(category),
      loading: selectors.isLoading(category),
      error: selectors.getError(category),
      hasMore: selectors.hasMore(category)
    };
    return acc;
  }, {});

  const isAnyLoading = selectors.isAnyLoading();

  const fetchAllCategories = useCallback(
    async (customOptions = {}) => {
      const fetchOptions = { 
        ...otherOptions, 
        ...customOptions, 
        ...(theme && { theme }), // Include theme if provided
        ...(language && { language }) // Include language if provided
      };
      const promises = categories.map(category =>
        actions.fetchVideosByCategory(category, fetchOptions)
      );
      return Promise.allSettled(promises);
    },
    [actions, categories, otherOptions, theme, language]
  );

  const refreshAllCategories = useCallback(() => {
    categories.forEach(category => actions.resetCategory(category));
    return fetchAllCategories();
  }, [actions, categories, fetchAllCategories]);
  // Utilisez useRef pour éviter les re-renders lors du premier chargement
  const hasInitialized = React.useRef(false);
  const previousTheme = React.useRef(theme);
  const previousLanguage = React.useRef(language);

  useEffect(() => {
    if (autoFetch && categories.length > 0) {
      // Check if theme or language has changed
      const themeChanged = previousTheme.current !== theme;
      const languageChanged = previousLanguage.current !== language;
      
      if (themeChanged || languageChanged || !hasInitialized.current) {
        const shouldFetch = categories.some(category => {
          const videos = selectors.getVideosByCategory(category);
          const loading = selectors.isLoading(category);
          const error = selectors.getError(category);
          return videos.length === 0 && !loading && !error;
        });

        if (shouldFetch || themeChanged || languageChanged) {
          hasInitialized.current = true;
          previousTheme.current = theme;
          previousLanguage.current = language;
          
          if (themeChanged) {
            console.log(`🎨 Theme changed to ${theme}, clearing cache and refreshing all categories`);
            clearCache();
            refreshAllCategories();
          } else if (languageChanged) {
            console.log(`🌐 Language changed to ${language}, clearing cache and refreshing all categories`);
            clearCache();
            refreshAllCategories();
          } else {
            fetchAllCategories();
          }
        }
      }
    }
  }, [autoFetch, categories, fetchAllCategories, refreshAllCategories, selectors, theme, language]);

  return {
    data,
    isAnyLoading,
    fetchAllCategories,
    refreshAllCategories
  };
};

export default {
  useVideos,
  useFeaturedVideo,
  useCurrentVideo,
  useVideoSearch,
  useInfiniteVideos,
  useMultipleVideoCategories
};
